import { useEffect, useMemo, useState } from "react";
// REDUX
import { useGetActionBanksByBankQuery } from "../../redux/actions/actionBankAPI";
// AUTHORIZATION
import GETJwtToken from "../../redux/authentication/authentication";
// MAPPERS
import { actionBankRowsToBankActions } from "../../pages/monitoringManagement/Actions/actionBankRows";
import { normalizeActionsMessageIds } from "../../pages/monitoringManagement/Actions/normalizeActionMessages";
// INTERFACES
import { BankActionInterface } from "../../interfaces/Bank.interface";

/**
 * The bank's configured actions, from the one endpoint that can answer for them.
 *
 * Every monitoring surface that asks "which control commands is this bank set up
 * for?" reads this hook, so the answer cannot differ between them. It used to:
 * ControlPanelLiftActions fetched `/action-banks/by-bank`, while the bank Hall
 * Calls and VIP panels and the lift Car Calls panel read `bankSelected.actions`
 * — the GORM-preloaded relation on `GET /banks/:id`. Nested relations exist only
 * on the JSON path, so on the FlatBuffer path that array is absent entirely, and
 * even when present it carries no `message_value` for the enum to resolve from.
 * The result was a bank whose Actions tab listed a hall-call command while its
 * Hall Calls tab said none was configured.
 *
 * Rows are mapped and normalised here, at the boundary, exactly as
 * ControlPanelLiftActions did privately: `actionBankRowsToBankActions` fixes the
 * shape, `normalizeActionsMessageIds` rewrites bound commands into the catalog id
 * space every consumer downstream already looks up.
 */
export default function useBankActions(bankId: string): {
  /** The bank's non-deleted-inclusive action rows, normalised. */
  actions: BankActionInterface[];
  /**
   * True once the query has actually answered.
   *
   * This is the signal a caller needs before rendering "nothing is configured".
   * An empty `actions` array cannot carry it: a drawer mounted against a blank
   * bank and a bank that genuinely has no actions look identical, and showing
   * the empty state during load flashes a configuration error on every open.
   */
  loaded: boolean;
  isLoading: boolean;
} {
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    GETJwtToken().then(setToken);
  }, []);

  const { data, isLoading, isFetching, isSuccess } = useGetActionBanksByBankQuery(
    { bankId, token },
    { skip: !token || !bankId },
  );

  const actions = useMemo(
    () => normalizeActionsMessageIds(actionBankRowsToBankActions(data)),
    [data],
  );

  return {
    actions,
    loaded: isSuccess,
    isLoading: isLoading || isFetching,
  };
}
